import { useQueryClient } from "@tanstack/react-query"
import { Archive, ArchiveRestore, Check, Copy, Flag, LogOut } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"
import type { DropdownAction } from "@/components/mobile/MobileTopNav"
import { useCommunityJoinActions } from "@/features/communities/hooks/useCommunityMembership"
import {
  adminCompleteRitual,
  archiveRitual,
  unarchiveRitual,
} from "@/features/rituals/services/ritual.service"
import { leaveRitual } from "@/features/rituals/services/ritual-membership.api"

interface MoreActionsParams {
  currentPath: string
  isMainNav: boolean
  isSettingsRoute: boolean
  isCommunityDetail: boolean
  isOtherProfilePage: boolean
  isRitualDetail: boolean
  ritualUIData:
    | {
        isArchived?: boolean
        isOwner?: boolean
        isOrganizer?: boolean
        membershipDetails?: { isMember?: boolean } | null
        canEdit?: boolean
        isAdminCompleted?: boolean
        id?: string
        title?: string
        media?: Array<{ url?: string | null }>
      }
    | undefined
  ritualId: string | undefined
  userId: string | undefined
  communityData:
    | { isJoined?: boolean; permissions?: { isOwner?: boolean }; isModerator?: boolean }
    | undefined
  communityId: string | undefined
}

interface MoreActionsResult {
  moreActions: DropdownAction[] | undefined
}

export function useMoreActions({
  currentPath,
  isMainNav,
  isSettingsRoute,
  isCommunityDetail,
  isOtherProfilePage,
  isRitualDetail,
  ritualUIData,
  ritualId,
  userId,
  communityData,
  communityId,
}: MoreActionsParams): MoreActionsResult {
  const queryClient = useQueryClient()
  const { leave: leaveCommunity } = useCommunityJoinActions()

  const moreActions = useMemo<DropdownAction[] | undefined>(() => {
    if (isMainNav || isSettingsRoute) return undefined
    const actions: DropdownAction[] = []

    if (isCommunityDetail) {
      actions.push({
        label: "Copy Link",
        icon: Copy,
        onClick: () => {
          navigator.clipboard.writeText(window.location.href)
          toast.success("Link copied to clipboard")
        },
      })
      actions.push({
        label: "Report",
        icon: Flag,
        className: "text-brand-terracotta",
        onClick: () => {
          // This will be handled by the LayoutModals component
        },
      })
      if (
        communityData?.isJoined &&
        !communityData.permissions?.isOwner &&
        !communityData.isModerator
      ) {
        actions.push({
          label: "Leave Community",
          icon: LogOut,
          className: "text-brand-terracotta",
          onClick: () => {
            if (communityId) {
              leaveCommunity(communityId)
            }
          },
        })
      }
    }

    if (/^\/coaching-sessions\/[^/]+$/.test(currentPath)) {
      actions.push({
        label: "Share",
        onClick: () => navigator.clipboard.writeText(window.location.href),
      })
    }

    if (isOtherProfilePage) {
      const profileUserId = currentPath.split("/")[2]
      const isOwnProfile = userId === profileUserId
      if (!isOwnProfile) {
        actions.push({
          label: "Copy Link",
          icon: Copy,
          onClick: () => {
            navigator.clipboard.writeText(window.location.href)
            toast.success("Link copied to clipboard")
          },
        })
        actions.push({
          label: "Report User",
          icon: Flag,
          className: "text-brand-terracotta",
          onClick: () => {
            // This will be handled by the LayoutModals component
          },
        })
      }
    }

    if (isRitualDetail && ritualUIData) {
      actions.push({
        label: ritualUIData?.isArchived ? "Unarchive" : "Archive",
        icon: ritualUIData?.isArchived ? ArchiveRestore : Archive,
        onClick: async () => {
          if (!ritualId) return
          try {
            if (ritualUIData?.isArchived) {
              await unarchiveRitual(ritualId)
              toast.success("Ritual unarchived")
            } else {
              await archiveRitual(ritualId)
              toast.success("Ritual archived")
            }
            queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
          } catch {
            toast.error("Failed to update ritual archive status")
          }
        },
      })
      actions.push({
        label: "Report Ritual",
        icon: Flag,
        className: "text-brand-terracotta",
        onClick: () => {
          // This will be handled by the LayoutModals component
        },
      })
      const canLeave =
        ritualUIData?.membershipDetails?.isMember &&
        !ritualUIData?.isOwner &&
        !ritualUIData?.isOrganizer &&
        !ritualUIData?.isAdminCompleted
      if (canLeave) {
        actions.push({
          label: "Leave Ritual",
          icon: LogOut,
          className: "text-brand-terracotta",
          onClick: async () => {
            if (!ritualId) return
            try {
              await leaveRitual(ritualId)
              toast.success("You have left the ritual")
              queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
            } catch {
              toast.error("Failed to leave ritual")
            }
          },
        })
      }
      const canManage = ritualUIData?.isOwner || ritualUIData?.isOrganizer
      if (canManage) {
        actions.push({
          label: "Complete",
          icon: Check,
          onClick: async () => {
            if (!ritualId) return
            try {
              await adminCompleteRitual(ritualId)
              toast.success("Ritual completed")
              queryClient.invalidateQueries({ queryKey: ["ritual", ritualId] })
            } catch {
              toast.error("Failed to complete ritual")
            }
          },
        })
      }
    }

    return actions.length > 0 ? actions : undefined
  }, [
    currentPath,
    isMainNav,
    isSettingsRoute,
    isCommunityDetail,
    isOtherProfilePage,
    isRitualDetail,
    ritualUIData,
    ritualId,
    userId,
    communityData,
    communityId,
    leaveCommunity,
    queryClient,
  ])

  return { moreActions }
}
